// The IRC layout's nick list (#154), inside the members <aside> that Server.tsx draws: grouped @ (owner and admins),
// + (online) and the rest, each alphabetical, each nick in its color. On a phone the aside slides in from the right
// (the `@N` button); its group labels show there. A person's row opens the same member menu as the other layouts.
// Part of the IRC layout: deleting components/irc/ and its entries removes it.
import type { Member } from "@bloom/shared";
import type { BotInfo } from "@bloom/shared";
import { CloseButton } from "../icons.tsx";
import { groupNicks, nickColor, nickWithMark, type NickEntry } from "./ircLogic.ts";

export function IrcNickList({ title, entries, me, members, bots, onMember, onBot, onClose }: {
  /** "#general", for the phone sheet's head. */
  title: string;
  entries: NickEntry[];
  me: string | null;
  members: Member[];
  bots: BotInfo[];
  onMember: (m: Member, rect: DOMRect) => void;
  onBot: (b: BotInfo, rect: DOMRect) => void;
  onClose: () => void;
}) {
  const groups = groupNicks(entries);
  return (
    <>
      <div className="members-head mobile-only">
        <strong>{title} <span className="irc-dim">[{entries.length}]</span></strong>
        <CloseButton onClick={onClose} aria-label="Close nick list" />
      </div>
      {groups.map((g) => (
        <section key={g.label} className="irc-nick-group" data-mark={g.mark || "none"} aria-label={g.label}>
          <div className="irc-nick-label">{g.label}</div>
          {g.nicks.map((n) => {
            const member = members.find((m) => m.id === n.id);
            const bot = bots.find((b) => b.id === n.id);
            const content = (
              <>
                <span className="irc-mark">{g.mark}</span>
                <span className="irc-nick-name" style={{ color: g.mark === "" ? undefined : nickColor(n.id) }}>{n.name}</span>
                {n.id === me && <span className="irc-dim irc-note">you</span>}
                {n.bot && <span className="irc-dim irc-note">bot</span>}
                {n.role === "owner" && <span className="irc-dim irc-note">owner</span>}
              </>
            );
            const label = nickWithMark(g.mark, n.name);
            if (member) return <button key={n.id} type="button" className="member irc-nick-row" aria-label={label} onClick={(e) => onMember(member, e.currentTarget.getBoundingClientRect())}>{content}</button>;
            if (bot) return <button key={n.id} type="button" className="member irc-nick-row" aria-label={label} onClick={(e) => onBot(bot, e.currentTarget.getBoundingClientRect())}>{content}</button>;
            return <div key={n.id} className="member irc-nick-row" aria-label={label}>{content}</div>;
          })}
        </section>
      ))}
    </>
  );
}

/** "[8] [+nt]" after a channel's name in the header. */
export function IrcModes({ count, modes }: { count: number; modes: string }) {
  return <span className="irc-modes irc-dim">[{count}] {modes}</span>;
}
